<template>
  <Layout>
    <ModalFiscalValidationNFSe v-if="fiscalValidation" />
    <ModalCancelNFSe />
    <Title
      title="Nota Fiscal de Serviço"
      newRegisterButtonName="Nova NFS-e"
      :hasNewRegister="true"
      :routeNewRegister="newNfse"
      hasPrint
    />
    <Table
      :key="tableKey"
      :tableData="tableData"
      :items="summaryNfses"
      :pagination="true"
      :perPage="paginationNfse.perPage"
      :startItem="paginationNfse.startItem"
      :endItem="paginationNfse.endItem"
      :totalItems="paginationNfse.totalItems"
      :page="paginationNfse.currentPage"
      :onClickRow="editNfse"
      :onSearch="search"
      :tooltipStatusField="'message'"
      @changeItemsPerPage="changeItemsPerPage"
      @changePage="changePage"
    />
  </Layout>
</template>

<script>
import { Layout, Title, Table, TableData, Actions } from '@/components';
import { mapActions, mapState } from 'vuex';
import { nfseStatusEnum } from '@/modules/Fiscal/modules/Nfse/enums';
import ModalCancelNFSe from '../components/modals/ModalCancelNFSe.vue';
import ModalFiscalValidationNFSe from '../components/modals/ModalFiscalValidationNFSe.vue';

export default {
  name: 'NFSe',
  components: {
    Layout,
    Title,
    Table,
    ModalCancelNFSe,
    ModalFiscalValidationNFSe,
  },
  computed: {
    ...mapState('nfse', [
      'summaryNfses',
      'paginationNfse',
      'warningsNfse',
      'fiscalValidation',
      'tableKey',
    ]),
    ...mapState('nfseComponents', ['modalCancelNfseIsOpen']),
    tableData() {
      const { Data, TABLE_CELL_ALIGN, TABLE_CELL_CUSTOM } = TableData;
      const DataTable = new Data({
        panelActions: true,
        panelActionsSearch: true,
        select: true,
        titleMobile: (item) => item.description,
        onSearch: this.search,
      });

      DataTable.addField({
        key: 'internalNumber',
        label: 'Nº Interno',
        sortable: true,
        width: 210,
        align: TABLE_CELL_ALIGN.center,
      })
        .addField({
          key: 'nfseNumber',
          label: 'Nº NFS-e',
          sortable: true,
          width: 230,
          align: TABLE_CELL_ALIGN.center,
        })
        .addField({
          key: 'corporateName',
          label: 'Cliente',
          sortable: true,
          width: 350,
          align: TABLE_CELL_ALIGN.center,
        })
        .addField({
          key: 'natureOfOperation',
          label: 'Natureza da Operação',
          sortable: true,
          width: 300,
          align: TABLE_CELL_ALIGN.center,
        })
        .addField({
          key: TABLE_CELL_CUSTOM.status,
          label: 'Status',
          width: 100,
          align: TABLE_CELL_ALIGN.center,
          formatter: (value, field, items) => this.buildStatus(items),
        })
        .addField({
          key: 'issueDate',
          label: 'Emissão',
          sortable: true,
          width: 150,
          align: TABLE_CELL_ALIGN.center,
          formatter: (value) => this.$moment(value).format('DD/MM/YYYY'),
        })
        .addField({
          key: 'total',
          label: 'Total R$',
          sortable: true,
          width: 150,
          align: TABLE_CELL_ALIGN.center,
          formatter: (value) =>
            new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(value),
        })
        .addActions(Actions('cancel', (selected) => this.cancel(selected)))
        .addActions(Actions('consult', (selected) => this.consult(selected)))
        .addActions(Actions('getXml', (selected) => this.getXml(selected)))
        .addActions(Actions('getDanfse', (selected) => this.getDanfse(selected)))
        .addActions(Actions('sendEmail', (selected) => this.sendEmail(selected)));
      return DataTable;
    },
  },
  async mounted() {
    await this.resetNfseState();
    await this.getFiscalValidation();

    if (this.fiscalValidation.invalid) {
      this.toggleModalFiscalValidation();
    }

    try {
      this.setLoading(true);
      await this.setSummaryNfses({
        pagination: this.paginationNfse,
      });
    } finally {
      this.setLoading(false);
    }
  },
  methods: {
    ...mapActions('application', ['setLoading']),
    ...mapActions('nfseComponents', [
      'toggleModalFiscalValidation',
      'toggleModalCancelNfseId',
      'setModalCancelNfseId',
    ]),
    ...mapActions('nfse', [
      'resetNfseState',
      'setSummaryNfses',
      'consultNfse',
      'findXmlUrl',
      'generateDanfse',
      'sendNfseEmail',
      'getFiscalValidation',
    ]),

    newNfse() {
      if (this.fiscalValidation.invalid) {
        this.toggleModalFiscalValidation();
        return;
      }

      this.$router.push({ name: 'NFSeRegister' });
    },
    editNfse(item) {
      if (!item.id) return;

      if (item.status === nfseStatusEnum.PROCESSING) {
        this.$alert.warning('NFS-e em processamento!');
        return;
      }

      this.$router.push({ name: 'NFSeEdit', params: { id: item.id } });
    },
    async getXml(item) {
      const [{ id, status }] = item;
      if (status === nfseStatusEnum.PROCESSING) {
        this.$alert.warning('NFS-e em processamento!');
        return;
      }

      const url = await this.findXmlUrl(id);
      if (!url) {
        this.$alert.warning('XML não encontrado!');
        return;
      }

      window.open(url, '_blank').focus();
    },
    async getDanfse(item) {
      const [{ id, status }] = item;
      if (status !== nfseStatusEnum.AUTHORIZED) {
        this.$alert.warning('NFS-e não autorizada!');
        return;
      }

      const url = await this.generateDanfse(id);
      if (!url) {
        this.$alert.warning('DANFSe não encontrada!');
        return;
      }

      window.open(url, '_blank').focus();
    },
    async sendEmail(item) {
      const [{ id, status }] = item;
      if (status !== nfseStatusEnum.AUTHORIZED) {
        this.$alert.warning('NFS-e não autorizada!');
        return;
      }

      this.setLoading(true);
      try {
        await this.sendNfseEmail(id);
        this.$alert.success('E-mail enviado com sucesso!');
      } finally {
        this.setLoading(false);
      }
    },
    async cancel(item) {
      const [{ status }] = item;

      if (status === nfseStatusEnum.CANCELED) {
        this.$alert.warning('NFS-e está cancelada!');
        return;
      }

      this.setModalCancelNfseId(item);
      this.toggleModalCancelNfseId();
    },
    async consult(item) {
      const [{ id, status }] = item;

      if (status === nfseStatusEnum.CANCELED) {
        this.$alert.warning('NFS-e está cancelada!');
        return;
      }

      await this.consultNfse(id);

      this.$alert.success('Consultando NFS-e...');
    },
    async search(value) {
      const search = {
        search: value,
        pagination: { ...this.paginationNfse, currentPage: 1 },
      };

      this.setLoading(true);
      await this.setSummaryNfses(search);
      this.setLoading(false);
    },
    async changeItemsPerPage(items) {
      const pagination = {
        ...this.paginationNfse,
        perPage: items,
        currentPage: 1,
      };
      this.setLoading(true);
      await this.setSummaryNfses({ pagination });
      this.setLoading(false);
    },
    async changePage(page) {
      const pagination = {
        ...this.paginationNfse,
        currentPage: page,
      };
      this.setLoading(true);
      await this.setSummaryNfses({ pagination });
      this.setLoading(false);
    },
  },
  watch: {
    modalCancelNfseIsOpen() {
      if (!this.modalCancelNfseIsOpen) {
        this.tableKey += 1;
      }
    },
  },
};
</script>
